import React, { useState, useEffect, useRef } from "react";

import styled from "styled-components";
import { rgba } from "polished";

import CigaretteSvg from "../assets/svg/cigarette.svg";
import HamburgerSvg from "../assets/svg/hamburger.svg";
import BeerSvg from "../assets/svg/beer.svg";
import CouchSvg from "../assets/svg/couch.svg";
import { theme } from "~/theme";
import { Tooltips } from ".";

const StyledList = styled.ol`
  list-style: none;
  counter-reset: count;
  padding: 0;
`;

const StyledListItem = styled.li`
  counter-increment: count;
  margin-bottom: 0.5rem;
  padding-left: 0.5rem;
  ::before {
    content: counter(count) ". ";
    font-family: FeltTipRoman, Chalkboard, Marker Felt, cursive;
    color: ${(p) => p.theme.warning};
    padding-right: 0.5rem;
  }
`;

type Risk = {
  title: string;
  icon: string;
  description: JSX.Element;
  link: JSX.Element;
  didYouKnow: JSX.Element;
  summary: string | null;
};

const ItemDescription: React.FC<{ risk: Risk }> = ({ risk }) => {
  return (
    <RiskInformation
      css={`
        margin: 0 -2rem 2rem;
        padding: 3rem;
        text-align: left;
        background-color: ${rgba(theme.gold, 0.2)};
      `}
    >
      <div className="text-center mb-4">
        <h1 className="h2 text-warning mb-3">{risk.title}</h1>
        {risk.description}
        {risk.link}
      </div>
      {!!risk.didYouKnow && (
        <div className="">
          <h2 className="text-warning text-center text-uppercase mb-3">
            Did you know?
          </h2>
          {risk.didYouKnow}
        </div>
      )}
    </RiskInformation>
  );
};

export const RiskFactors: React.FC<{}> = () => {
  const [itemSelected, setItemSelected] = useState<number>(0);

  const RISK_DATA = [
    {
      title: "Physical inactivity",
      icon: CouchSvg,
      description: (
        <p className="d-block mb-2">
          With changes in transportation and technology, and with many types of work (e.g. office work) and leisure activities becoming increasingly sedentary, there has been a decline in physical activity and an increase in sitting. High levels of sedentary behaviour are linked to increasing rates of overweight and obesity. Low levels of physical activity has been shown to increase an individual’s risk of cardiovascular disease, some cancers, type 2 diabetes and osteoporosis.<Tooltips id={5} />
        </p>
      ),
      link: (
        <strong>
          For more information, check out the{" "}
          <a
            className="text-reset text-underline"
            href="https://www.health.gov.au/health-topics/physical-activity-and-exercise/physical-activity-and-exercise-guidelines-for-all-australians/for-adults-18-to-64-years"
            target="_blank"
            rel="noreferrer noopener"
          >
            Physical Activity and Sedentary Behaviour Guidelines
          </a>
        </strong>
      ),
      didYouKnow: (
        <ul className="bullet-fulcrum bullet-gold mb-4">
          <li>
            <span>
              Worldwide, physical inactivity is the 4th leading risk factor for
              mortality, contributing to 6% of deaths.
              <Tooltips id={6} />
            </span>
          </li>
          <li>
            <span>
              In Australia, physical inactivity is the 5th leading risk factor for men and the 3rd leading risk factor for women.
              <Tooltips id={7} />
            </span>
          </li>
          <li>
            <span>
              Approximately 1 in 2 Australian adults (55%) do not exercise
              enough to meet physical activity guidelines.
              <Tooltips id={8} />
            </span>
          </li>
        </ul>
      ),
      summary: null,
    },
    {
      title: "Unhealthy eating and drinking",
      icon: HamburgerSvg,
      description: (
        <p className="d-block mb-2">
          Poor nutrition relates to the inadequate intake of nutrient-rich
          foods such as fruit, vegetables, wholegrain breads and cereals, as
          well as the excessive consumption of junk food (high in energy or
          kilojoules, saturated fat, sugar and salt, and low in nutrients).
          <Tooltips id={9} /> It can result in adverse health outcomes such as
          obesity, high blood pressure and high cholesterol.
          <Tooltips id={9} /> These health outcomes are widely recognised as
          Australia’s leading health concerns as they are strongly linked with
          chronic diseases, such as type 2 diabetes, heart disease, stroke and
          some cancers.
          <Tooltips id={9} />
        </p>
      ),
      link: (
        <strong className="mb-3">
          For more information, check out the{" "}
          <a
            className="text-reset text-underline"
            href="https://www.eatforhealth.gov.au/guidelines"
            target="_blank"
            rel="noreferrer noopener"
          >
            Australian Dietary Guidelines
          </a>
        </strong>
      ),
      didYouKnow: (
        <ul className="bullet-fulcrum bullet-gold mb-4">
          <li>
            <span>
              Only 51% of Australian adults meet the daily recommendation of 2
              serves of fruit.
              <Tooltips id={10} />
            </span>
          </li>
          <li>
            <span>
              Only 8% of Australian adults meet the daily recommendation of 5
              serves of vegetables.
              <Tooltips id={11} />
            </span>
          </li>
          <li>
            <span>
              Adults are getting 35% of their daily energy (kilojoule)
              requirements from junk foods and drinks, including alcoholic
              drinks, cakes, chocolate, pastries, biscuits, sugary drinks, chips
              and meat pies.
              <Tooltips id={12} />
            </span>
          </li>
          <li>
            <span>
              1 in 3 Australian adults has high cholesterol.
              <Tooltips id={12} />
            </span>
          </li>
          <li>
            <span>
              1 in 5 Australian adults has high blood pressure.
              <Tooltips id={10} />
            </span>
          </li>
          <li>
            <span>
              Two thirds (67%) of Australian adults are overweight or obese.
              Rates are higher in men (75%) compared to women (60%).
              <Tooltips id={10} />
            </span>
          </li>
        </ul>
      ),
      summary:
        "Eating a diet high in nutrient-rich foods, such as fruit and vegetables, and limiting the intake of junk food to only occasionally and in small amounts, will assist in maintaining a healthy weight and help prevent the onset of chronic disease.",
    },
    {
      title: "Smoking",
      icon: CigaretteSvg,
      description: (
        <p className="d-block mb-2">
          Smoking is a leading cause of death and disease in Australia,
          resulting in cancers, heart disease, respiratory disease and many
          other damaging and potentially fatal conditions.
          <Tooltips id={3} /> Passive smokers, who unintentionally inhale
          second-hand smoke are at risk too.
          <Tooltips id={13} />
        </p>
      ),
      link: (
        <strong className="mb-3">
          For more information, check out the{" "}
          <a
            className="text-reset text-underline"
            href="https://www.commerce.wa.gov.au/worksafe/smoking"
            target="_blank"
            rel="noreferrer noopener"
          >
            information on environmental tobacco smoke (ETS) in the workplace
          </a>{" "}
          and the{" "}
          <a
            className="text-reset text-underline"
            href="https://www.commerce.wa.gov.au/publications/national-guidance-note-elimination-environmental-tobacco-smoke-workplace-nohsc-30192003"
            target="_blank"
            rel="noreferrer noopener"
          >
            National guidance note on the elimination of ETS in the workplace
          </a>
          .
        </strong>
      ),
      didYouKnow: (
        <ul className="bullet-fulcrum bullet-gold mb-4">
          <li>
            <span>
              8.6% of Western Australians over the age of 18 smoke daily
              <Tooltips id={14} />
            </span>
          </li>
          <li>
            <span>
              Nearly 1,700 Western Australians die each year from smoking-related causes which is almost 5 deaths every day.
              <Tooltips id={15} />
            </span>
          </li>
          <li>
            <span>
              Smoking costs Australia a staggering $137 billion each year, $5 billion of this is borne by businesses.
              <Tooltips id={16} />
            </span>
          </li>
        </ul>
      ),
      summary: null,
    },
    {
      title: "Alcohol use",
      icon: BeerSvg,
      description: (
        <p className="d-block mb-2">
          Drinking alcohol can significantly affect long term health. There are
          several chronic diseases associated with alcohol consumption,
          including cancer (e.g. bowel, breast, throat, mouth, oesophagus,
          liver), heart disease, liver disease, stroke and mental health
          problems.
          <Tooltips id={17} />
          <sup>,</sup>
          <Tooltips id={18} />
        </p>
      ),
      link: (
        <strong className="mb-3">
          For more information, check out{" "}
          <a
            className="text-reset text-underline"
            href="https://www.health.gov.au/health-topics/alcohol/about-alcohol/how-much-alcohol-is-safe-to-drink"
            target="_blank"
            rel="noreferrer noopener"
          >
            how much alcohol is safe to drink?
          </a>
        </strong>
      ),
      didYouKnow: (
        <ul className="bullet-fulcrum bullet-gold mb-4">
          <li>
            <span>
              1 in 5 (17.6%) Australian adults drink at levels that exceed the
              lifetime risk guidelines, which equates to more than 4 standard
              drinks a day.
              <Tooltips id={8} />
            </span>
          </li>
          <li>
            <span>
              About 90% of the Australian workforce population consumes alcohol.
              <Tooltips id={19} />
            </span>
          </li>
          <li>
            <span>
              Alcohol costs Australian workplaces an estimated $3.5 billion
              annually in lost productivity.
              <Tooltips id={19} />
            </span>
          </li>
          <li>
            <span>
              High-risk alcohol use reduces workplace productivity, safety and work relations, and increases absenteeism.
              <Tooltips id={19} />
            </span>
          </li>
        </ul>
      ),
      summary: null,
    },
  ];

  return (
    <>
      <div className="row">
        <div className="col-md-10 offset-md-1 col-lg-8 offset-lg-2">
          <div className="text-center">
            <h2 className="h1 text-center text-uppercase text-warning mb-4">Risk Factors</h2>
            <p>The key behavioural risk factors in workplace health are:</p>
            <p className="lead mb-3">
              Physical inactivity, unhealthy eating and drinking, smoking,
              alcohol use
            </p>
            <p className="mb-4">Having a good understanding of these risk factors and the relevant national guidelines will help you to design effective and evidence-based workplace health strategies.</p>
            <h3 className="text-primary mb-4">
              Click the symbols below to reveal more information.
            </h3>
          </div>
          <div className="">
            {RISK_DATA.map((risk, idx) => (
              <StyledButton
                id={risk.title}
                key={idx}
                className="col-6 col-md-3"
                onClick={() => setItemSelected(idx)}
              >
                <CircleDiv active={idx === itemSelected}>
                  <IconDiv
                    className={idx === itemSelected ? "bg-gold" : "bg-grey"}
                  >
                    <img src={risk.icon} alt={`${risk.title} icon`} />
                  </IconDiv>
                </CircleDiv>
              </StyledButton>
            ))}
          </div>
        </div>
      </div>
      <ItemDescription risk={RISK_DATA[itemSelected]} />
    </>
  );
};

const StyledButton = styled.button`
  background-color: transparent;
  border: none;
  &:focus {
    outline: none;
  }
`;

const CircleDiv = styled.div<{ active: boolean }>`
  position: relative;
  width: 100%;
  padding-bottom: 100%;
  transition: transform 500ms cubic-bezier(0.34, 1.86, 0.64, 1);
  transform: ${(props) => (props.active ? "scale(1.5)" : "scale(1)")};
`;

const IconDiv = styled.div`
  position: absolute;
  top: 10%;
  left: 10%;
  bottom: 10%;
  right: 10%;
  padding: 1rem;
  border-radius: 50%;
`;

const RiskInformation = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  text-align: center;
`;
